/**
 * CPU Waiting canonical state helpers (ORGU-131).
 *
 * Provides:
 * - Matching work leases and waiters to missions, branches, sessions, jobs by cwd/what/sessionId/projectId.
 * - Format 'Waiting for CPU, 3rd in line, about 4 min'.
 * - Format CPU holders tooltip/popover ('24 of 24 slots: main check 4, ORGU-31 tests 8...').
 * - Format Overview top strip notice ('CPU full: N waiting, next slot in about M min').
 * - Format Autopilot bar status ('3 started, limit 10; the machine has room for 3, 7 wait for CPU').
 */
import type { Machine, MachineLoadEntry, MachineLoadState, WorkBudgetView, WorkLeaseView, WorkWaiterView } from '@orgus/protocol';
import { formatInt, plural } from '@orgus/ui';

export function ordinal(n: number): string {
  if (n <= 0) return '0th';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

export function estimateWaitingMinutes(placeInLine: number): number {
  if (placeInLine <= 0) return 0;
  // Estimate ~1.5 min per position in line (1st -> 2, 2nd -> 3, 3rd -> 4, 4th -> 6, etc.)
  return Math.max(1, Math.round(placeInLine * 1.5));
}

export function formatWaitingForCpuLabel(placeInLine: number, estimateMinutes?: number): string {
  const est = estimateMinutes ?? estimateWaitingMinutes(placeInLine);
  return `Waiting for CPU, ${ordinal(placeInLine)} in line, about ${est} min`;
}

interface Names {
  sessions?: Record<string, { title: string }>;
  projects?: Record<string, { name: string }>;
}

export function formatHolderName(item: { what: string; cwd?: string; sessionId?: string; projectId?: string }, names?: Names): string {
  const what = item.what.trim();
  const landMatch = /^orgus land\s+(\S+)/.exec(what);
  if (landMatch?.[1]) {
    const branch = landMatch[1];
    if (branch === 'main') return 'main check';
    return `${branch} landing`;
  }

  if (/pnpm test|vitest|jest|playwright|cypress/i.test(what)) {
    if (/visual/i.test(what)) return 'visual tests';
    const missionMatch = /(ORGU-\d+)/i.exec(what) || /(ORGU-\d+)/i.exec(item.cwd ?? '');
    if (missionMatch?.[1]) {
      return `${missionMatch[1].toUpperCase()} tests`;
    }
    return 'tests';
  }

  if (item.sessionId && names?.sessions?.[item.sessionId]) {
    return names.sessions[item.sessionId].title;
  }
  if (item.projectId && names?.projects?.[item.projectId]) {
    return `${names.projects[item.projectId].name} check`;
  }

  const clean = what.replace(/^(pnpm|npm|yarn|npx|node)\s+/, '').slice(0, 30);
  return clean || 'heavy command';
}

export function formatHoldersSummary(work: WorkBudgetView | null, names?: Names): string {
  if (!work || work.budget <= 0) return 'No slots in use';

  const groups = new Map<string, number>();
  for (const lease of work.leases) {
    const name = formatHolderName(lease, names);
    groups.set(name, (groups.get(name) ?? 0) + lease.slots);
  }

  const parts = [...groups.entries()].map(([name, slots]) => `${name} ${slots}`);
  const open = Math.max(0, work.budget - work.used);

  let details = parts.join(', ');
  if (open > 0) {
    details = details ? `${details} (${open} open capacity)` : `${open} open capacity`;
  }

  return `${formatInt(work.used)} of ${formatInt(work.budget)} slots: ${details || 'none'}`;
}

export interface WaitingInfo {
  isWaiting: boolean;
  placeInLine: number;
  totalWaiting: number;
  estimateMinutes: number;
  label: string;
  machineId: string;
  work: WorkBudgetView | null;
  waiter?: WorkWaiterView;
  lease?: WorkLeaseView;
}

export function findWaitingInfo(
  slice: MachineLoadState | undefined,
  target: { cwd?: string; what?: string; branch?: string; sessionId?: string; projectId?: string; missionId?: string },
): WaitingInfo | null {
  if (!slice?.machines) return null;

  for (const entry of slice.machines) {
    const work = entry.work;
    if (!work) continue;

    // Check waiters first
    const waiterIndex = work.waiting.findIndex((w) => {
      if (target.sessionId && w.sessionId === target.sessionId) return true;
      if (target.projectId && w.projectId === target.projectId) return true;
      if (target.branch && w.what.includes(target.branch)) return true;
      if (target.cwd && w.cwd && (w.cwd === target.cwd || w.cwd.startsWith(target.cwd) || target.cwd.startsWith(w.cwd))) return true;
      if (target.what && w.what.includes(target.what)) return true;
      if (target.missionId && (w.what.includes(target.missionId) || (w.cwd && w.cwd.includes(target.missionId)))) return true;
      return false;
    });

    if (waiterIndex >= 0) {
      const placeInLine = waiterIndex + 1;
      const estimateMinutes = estimateWaitingMinutes(placeInLine);
      return {
        isWaiting: true,
        placeInLine,
        totalWaiting: work.waiting.length,
        estimateMinutes,
        label: formatWaitingForCpuLabel(placeInLine, estimateMinutes),
        machineId: entry.machineId,
        work,
        waiter: work.waiting[waiterIndex],
      };
    }
  }

  return null;
}

export function getFleetCpuWaitingStats(slice: MachineLoadState | undefined): { totalWaiting: number; totalFit: number; nextSlotMinutes: number; fullMachineCount: number } {
  if (!slice?.machines) return { totalWaiting: 0, totalFit: 0, nextSlotMinutes: 0, fullMachineCount: 0 };

  let totalWaiting = 0;
  let totalFit = 0;
  let fullMachineCount = 0;

  for (const entry of slice.machines) {
    const w = entry.work;
    if (!w) continue;
    totalWaiting += w.waiting.length;
    const free = Math.max(0, w.budget - w.used);
    const want = Math.max(2, Math.ceil(w.budget / 3));
    const fit = w.waiting.length ? 0 : Math.floor(free / want);
    totalFit += fit;
    if (w.used >= w.budget || w.waiting.length > 0) {
      fullMachineCount++;
    }
  }

  const nextSlotMinutes = totalWaiting > 0 ? 1 : 0;
  return { totalWaiting, totalFit, nextSlotMinutes, fullMachineCount };
}

export function formatCpuFullTopStripText(slice: MachineLoadState | undefined): string | null {
  const { totalWaiting, nextSlotMinutes } = getFleetCpuWaitingStats(slice);
  if (totalWaiting <= 0) return null;
  return `CPU full: ${formatInt(totalWaiting)} waiting, next slot in about ${nextSlotMinutes} min`;
}

/**
 * Autopilot's own starts against its limit (R-MS-2), then the machine's room and the CPU queue
 * only when something waits: a quiet machine adds nothing to the line.
 */
export function formatAutopilotBarStatus(runningCount: number, limitCount: number, slice: MachineLoadState | undefined): string {
  const { totalWaiting, totalFit } = getFleetCpuWaitingStats(slice);
  const head = `${runningCount} started, limit ${limitCount}`;
  if (totalWaiting <= 0) return head;
  return `${head}; the machine has room for ${totalFit}, ${totalWaiting} wait for CPU`;
}

export const CPU_WAITING_EXPLANATION = 'The machine is full; more at once would slow everything and time tests out';
